// BlockBodyEditor — TipTap rich-text editor for Composer Block bodies.
//
// Supports: bold, italic, hyperlink (with new-tab toggle, defaulting on),
// bulleted list, text alignment (left/center/right), indent / outdent.
// Outputs sanitized HTML via `sanitizeBlockHtml`.
import { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  AlignLeft,
  AlignCenter,
  AlignRight,
  IndentIncrease,
  IndentDecrease,
  Unlink,
} from "lucide-react";
import { Toggle } from "@/components/ui/toggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BlockIndent } from "@/lib/editor/indentExtension";
import {
  looksLikeHtml,
  plainTextToBlockHtml,
  sanitizeBlockHtml,
} from "@/lib/composerBlocks/sanitizeBlockHtml";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
};

const normalizeIncoming = (raw: string): string => {
  if (!raw) return "";
  return looksLikeHtml(raw) ? raw : plainTextToBlockHtml(raw);
};

export const BlockBodyEditor = ({ value, onChange, placeholder, minHeight = 160 }: Props) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        orderedList: false,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? "The text that will be inserted when this block is dragged into a draft.",
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { rel: "noopener noreferrer" },
        protocols: ["http", "https", "mailto", "tel"],
      }),
      TextAlign.configure({ types: ["paragraph"] }),
      BlockIndent,
    ],
    content: normalizeIncoming(value),
    onUpdate: ({ editor }) => {
      onChange(sanitizeBlockHtml(editor.getHTML()));
    },
  });

  // Reset content if the external value changes to something different than
  // what's in the editor (e.g. when the dialog is reopened on a different block).
  useEffect(() => {
    if (!editor) return;
    const next = normalizeIncoming(value);
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-input bg-background">
      <BlockBodyToolbar editor={editor} />
      <EditorContent
        editor={editor}
        className="block-body-editor px-3 py-2 text-sm leading-relaxed"
        style={{ minHeight }}
      />
    </div>
  );
};

type ToolbarProps = { editor: ReturnType<typeof useEditor> };

const BlockBodyToolbar = ({ editor }: ToolbarProps) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState("");
  const [linkNewTab, setLinkNewTab] = useState(true);

  const openLinkPopover = useCallback(() => {
    if (!editor) return;
    const existing = editor.getAttributes("link") as {
      href?: string;
      target?: string;
    };
    setLinkHref(existing.href ?? "");
    setLinkNewTab(existing.target ? existing.target === "_blank" : true);
    setLinkOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = linkHref.trim();
    if (!href) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setLinkOpen(false);
      return;
    }
    const attrs = linkNewTab
      ? { href, target: "_blank", rel: "noopener noreferrer" }
      : { href, target: null, rel: null };
    editor.chain().focus().extendMarkRange("link").setLink(attrs).run();
    setLinkOpen(false);
  }, [editor, linkHref, linkNewTab]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1">
      <Toggle
        size="sm"
        pressed={editor.isActive("bold")}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
        title="Bold (Cmd/Ctrl+B)"
      >
        <Bold className="h-3.5 w-3.5" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("italic")}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
        title="Italic (Cmd/Ctrl+I)"
      >
        <Italic className="h-3.5 w-3.5" />
      </Toggle>

      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Toggle
            size="sm"
            pressed={editor.isActive("link")}
            onPressedChange={openLinkPopover}
            aria-label="Hyperlink"
            title="Add or edit link"
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Toggle>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-3" align="start">
          <div className="space-y-1.5">
            <Label htmlFor="block-link-href" className="text-xs">URL</Label>
            <Input
              id="block-link-href"
              value={linkHref}
              onChange={(e) => setLinkHref(e.target.value)}
              placeholder="https://example.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="block-link-newtab"
              checked={linkNewTab}
              onCheckedChange={(v) => setLinkNewTab(v === true)}
            />
            <Label htmlFor="block-link-newtab" className="text-xs font-normal">
              Open in new window
            </Label>
          </div>
          <div className="flex items-center justify-between gap-2">
            {editor.isActive("link") ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  removeLink();
                  setLinkOpen(false);
                }}
              >
                <Unlink className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : <span />}
            <Button type="button" size="sm" onClick={applyLink}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Toggle
        size="sm"
        pressed={editor.isActive("bulletList")}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bulleted list"
        title="Bulleted list"
      >
        <List className="h-3.5 w-3.5" />
      </Toggle>

      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

      <Toggle
        size="sm"
        pressed={editor.isActive({ textAlign: "left" })}
        onPressedChange={() => editor.chain().focus().setTextAlign("left").run()}
        aria-label="Align left"
        title="Align left"
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive({ textAlign: "center" })}
        onPressedChange={() => editor.chain().focus().setTextAlign("center").run()}
        aria-label="Align center"
        title="Align center"
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive({ textAlign: "right" })}
        onPressedChange={() => editor.chain().focus().setTextAlign("right").run()}
        aria-label="Align right"
        title="Align right"
      >
        <AlignRight className="h-3.5 w-3.5" />
      </Toggle>

      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => editor.chain().focus().outdentBlock().run()}
        title="Outdent"
        aria-label="Outdent"
      >
        <IndentDecrease className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => editor.chain().focus().indentBlock().run()}
        title="Indent"
        aria-label="Indent"
      >
        <IndentIncrease className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

export default BlockBodyEditor;
